import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { api, API_URL } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];

export default function MosaicPage() {
  const { t } = useLanguage();
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("today");
  const [faces, setFaces] = useState(null);

  const load = () => {
    api.get("/mosaic/info").then((r) => setInfo(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    api.get("/mosaic/faces").then((r) => setFaces(r.data.faces)).catch(() => setFaces([]));
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = info ? (info.sold / info.total) * 100 : 0;
  const cacheBust = info ? `?v=${info.generated_at}` : "";
  const imgSrc = mode === "sim" ? `${API_URL}/mosaic-simulation.jpg${cacheBust}` : `${API_URL}/mosaic.jpg${cacheBust}`;

  return (
    <main className="min-h-screen bg-[#FAF8F5] pt-[57px] flex flex-col" data-testid="mosaic-page">
      <div className="px-5 md:px-12 py-10 border-b border-border/60 flex flex-wrap items-end justify-between gap-8">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-[10px] tracking-[0.4em] text-golddark mb-4"
          >
            {t("mosaic.eyebrow").toUpperCase()}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="font-display font-black tracking-tighter text-4xl md:text-6xl"
            data-testid="mosaic-title"
          >
            {t("mosaic.title")}
          </motion.h1>
          <p className="mt-3 text-xs text-muted-foreground max-w-lg leading-relaxed">{t("mosaic.sub")}</p>
        </div>
        <div className="text-right">
          <div className="font-mono text-4xl font-light text-golddark tabular-nums" data-testid="mosaic-progress">
            {progress.toFixed(3)}%
          </div>
          <div className="mt-1 text-[10px] tracking-[0.3em] text-muted-foreground">
            {info ? `${info.faces} ${t("mosaic.faces")} · ${info.sold.toLocaleString("it-IT")} / ${info.total.toLocaleString("it-IT")} PX` : "…"}
          </div>
          <div className="mt-4 w-56 h-px bg-border relative overflow-hidden ml-auto">
            <motion.div
              className="absolute left-0 top-0 h-full bg-[#7D8C77]"
              initial={{ width: 0 }}
              animate={{ width: `${Math.max(progress, 0.5)}%` }}
              transition={{ duration: 1.5, ease: EASE }}
            />
          </div>
        </div>
      </div>

      <div data-lenis-prevent className="relative flex-1 min-h-[70vh] bg-[#FFFFFF] border-b border-border/60 overflow-hidden">
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex border border-border bg-[#FAF8F5]/95"
          data-testid="mosaic-mode-toggle"
        >
          <button
            onClick={() => setMode("today")}
            data-testid="mosaic-mode-today"
            className={`px-5 py-2.5 text-[10px] tracking-[0.25em] transition-colors duration-200 ${
              mode === "today" ? "bg-[#3C3735] text-[#FAF8F5]" : "text-muted-foreground hover:bg-[#3C3735] hover:text-[#FAF8F5]"
            }`}
          >
            {t("mosaic.today").toUpperCase()}
          </button>
          <button
            onClick={() => setMode("sim")}
            data-testid="mosaic-mode-sim"
            className={`px-5 py-2.5 text-[10px] tracking-[0.25em] border-l border-border transition-colors duration-200 ${
              mode === "sim" ? "bg-[#3C3735] text-[#FAF8F5]" : "text-muted-foreground hover:bg-[#3C3735] hover:text-[#FAF8F5]"
            }`}
          >
            {t("mosaic.simulation").toUpperCase()}
          </button>
        </div>
        {mode === "sim" && (
          <div
            className="absolute top-4 right-5 z-10 px-3 py-2 bg-[#C5A880] text-[#3C3735] text-[9px] tracking-[0.3em] font-semibold"
            data-testid="mosaic-sim-badge"
          >
            {t("mosaic.simBadge")}
          </div>
        )}
        <TransformWrapper key={mode} minScale={0.5} maxScale={25} centerOnInit wheel={{ step: 0.12 }} doubleClick={{ mode: "zoomIn" }}>
          <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full">
            <img
              src={imgSrc}
              alt={t("alts.mosaic")}
              className="w-full block"
              data-testid="mosaic-image"
            />
          </TransformComponent>
        </TransformWrapper>
        <div className="absolute bottom-4 left-5 max-w-md text-[9px] tracking-[0.3em] text-muted-foreground bg-[#FAF8F5]/80 px-3 py-2 border border-border" data-testid="mosaic-caption">
          {(mode === "sim" ? t("mosaic.simNote") : t("mosaic.zoomHint")).toUpperCase()}
        </div>
      </div>

      <section className="px-5 md:px-12 py-14 border-t border-border/60" data-testid="mosaic-faces-section">
        <p className="text-[10px] tracking-[0.4em] text-golddark mb-6">{t("mosaic.galleryTitle").toUpperCase()}</p>
        {faces && faces.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-3" data-testid="mosaic-faces-grid">
            {faces.map((f) => (
              <div key={f.index} className="border border-border/60 p-2" data-testid={`mosaic-face-${f.index}`}>
                <img
                  src={`${API_URL}/files/${f.photo_path}`}
                  alt={`${t("alts.face")} — ${f.user_name || t("grid.anonymous")}`}
                  className="w-full aspect-square object-cover"
                />
                <div className="mt-2 font-mono text-[10px] text-golddark tabular-nums">[{f.x}, {f.y}]</div>
                <div className="text-[9px] tracking-[0.15em] text-muted-foreground truncate">
                  {(f.user_name || t("grid.anonymous")).toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="mosaic-faces-empty">
            {t("mosaic.galleryEmpty")}
          </p>
        )}
      </section>

      <div className="px-5 md:px-12 py-10 border-t border-border/60 flex flex-wrap items-center justify-between gap-6">
        <p className="text-[10px] tracking-[0.25em] text-muted-foreground max-w-xl leading-relaxed">
          {t("mosaic.note").toUpperCase()}
        </p>
        <Link
          to="/grid"
          data-testid="mosaic-cta-buy"
          className="px-8 py-4 bg-[#7D8C77] text-[#FAF8F5] text-xs tracking-[0.25em] font-semibold hover:bg-[#3C3735] transition-colors duration-200"
        >
          {t("hero.ctaBuy").toUpperCase()}
        </Link>
      </div>
    </main>
  );
}
